import * as Enumerable from 'linq';
import { DateTime } from 'luxon';
import { DynamicDialogConfig, DynamicDialogRef } from 'primeng/dynamicdialog';
import { filter, tap, firstValueFrom, withLatestFrom, catchError } from 'rxjs';
import { v4 } from 'uuid';

import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnChanges, Optional, SimpleChanges, ViewEncapsulation, effect, inject, input } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Activity, ActivityEntityName, ActivityKind, ActivityShareType, ActivityType, Attachment, DateUtility, DialogType, DomainType, Entity, FiltersRelativeDateUtility, MetadataAttachment, MetadataEntityData, UserSettingKey, getFileThumbnailUrl } from '@jms/model';
import { ConfirmServicePro } from '@workbuddy/ui/blocks/confirm';
import { ProgressModule } from '@jms/ui/ui.progress';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { MasterContentDirective, MasterDetailBlockComponent, MasterDetailEditorDirective, MasterDetailModalEditorDirective, MasterFilterContentDirective, MasterFooterContentDirective } from '@workbuddy/ui/blocks/master.detail';
import { ModuleHeaderApi, ModuleHeaderComponent, ModuleHeaderHost, ModuleViewHeaderCommandIds, ViewEntity } from '@workbuddy/ui/blocks/module.header';
import { AttachmentComponent, getAttachmentContext } from '@workbuddy/ui/components/attachment';
import { ButtonComponent, ButtonSize } from '@workbuddy/ui/components/button';
import { ViewOptionsBuilder, ViewOptionSchema, ViewOptionType } from '@workbuddy/ui/components/view.options/model';
import { Command } from '@workbuddy/ui/model';
import { ActiveService, DialogService, ElementRegistryService, MetadataService, SelectionService, ToastService } from '@workbuddy/ui/services';

import { AllowedPickType, canUserChangeFile, FilesContext, FilesDisplayMode } from '../../model';
import { countFiles, deleteFiles, deleteFilesQuestion, FilesFilterDataSourceValue, FilesService, FilesZipService, getFilesFilterDataSource } from '../../sevices';
import { FileDetailsEditorComponent } from '../file.details.editor/file.details.editor.component';
import { FileDetailsPreviewComponent } from '../file.details.preview';
import { FileGroup, FileListComponent } from '../file.list/file.list.component';
import { FileUploadEditorComponent } from '../file.upload.editor/file.upload.editor.component';
import { ConfigService } from '@jms/ui/ui.standalone.services/config.service';
import { CurrentUserService } from '@jms/ui/ui.standalone.services/current.user.service';
import { FilenameExtractService } from '@jms/utils/util.download';
import { AttachmentContext } from '@workbuddy/ui/components/attachment/model';
import { RightsService } from '@jms/utils/util.permissions';
;

enum FilesDashboardCommandIds {
    AddSelected = 'AddSelected',
    DownloadSelected = 'DownloadSelected',
    DownloadAll = 'DownloadAll',
    ClearSelected = 'ClearSelected',
    DeleteSelectedFiles = 'DeleteSelectedFiles',
    SelectAll = 'SelectAll',
}

interface ViewOptions {
    groupBy: string;
    sortBy: string;
    sortOrder: 'asc' | 'desc';
    showFileActivitiesOnly: boolean;
    sharing: ActivityShareType | 'All';
    showAll?: boolean;
}

interface MetadataAttachmentWithGroup extends MetadataAttachment {
    /** Entry of an array group field (tags, taskItems) this exploded row stands for; null = "No Group". */
    groupEntry?: Entity | null;
}

@UntilDestroy()
@Component({
    selector: 'files-dashboard',
    templateUrl: 'files.dashboard.component.html',
    styleUrls: ['files.dashboard.component.less'],
    encapsulation: ViewEncapsulation.None,
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,

        MasterDetailBlockComponent,
        MasterContentDirective,
        MasterFooterContentDirective,
        MasterDetailEditorDirective,
        MasterDetailModalEditorDirective,
        MasterFilterContentDirective,

        ModuleHeaderComponent,

        FileListComponent,
        FileDetailsEditorComponent,
        FileUploadEditorComponent,
        FileDetailsPreviewComponent,

        ButtonComponent,
        ProgressModule,
    ],
    providers: [
        SelectionService,
        ElementRegistryService,
        ActiveService,
        FilesService,
        DialogService,
    ],
    host: {
        '[class.files-picker]': 'context?.displayMode === FilesDisplayMode.Picker',
    }
})

export class FilesDashboardComponent implements OnChanges, ModuleHeaderHost {

    private metadataService = inject(MetadataService);
    private toastService = inject(ToastService);
    private attachmentMetadata: MetadataEntityData;

    @Input() public context: FilesContext;
    @Input() public allowMultiSelect = true;

    // The portal is mounted on the tenant host at /clients, so the hostname does not identify the app.
    // The office config carries no domainType, so an unset one is the tenant; otherwise the office picker never loaded its files (WBS-12090).
    private domainType = (ConfigService.environment.domainType as DomainType) ?? DomainType.Tenant;
    private isTenant = this.domainType === DomainType.Tenant;
    private isClientsPortal = this.domainType === DomainType.ClientPortal;
    private isContractorsPortal = this.domainType === DomainType.ContractorPortal;

    protected get entity() {
        return this.context?.entity;
    }

    protected get entityId() {
        return this.context?.entityId;
    }

    protected get entityName() {
        return `${this.context?.entityName ?? this.entity}`;
    }

    protected get activity() {

        return {
            _id: v4(),
            entity: this.entity,
            entityId: this.entityId,
            tags: this.context?.activityTags,
            attachments: [],
            createdBy: null,
            createDate: new Date(),
            system: false,
            active: true,
            note: null,
            kind: ActivityKind.File,
            type: ActivityType.File
        } as Activity
    }

    protected get host(): ModuleHeaderHost {
        return this;
    }

    protected get settingsKey() {

        if (!this.context || !this.isTenant) return null;

        if (this.context.displayMode == FilesDisplayMode.Picker) {
            return `${UserSettingKey.FilesDefaultView}-picker`;
        }

        let entity = this.entity;

        if ([ActivityEntityName.Job, ActivityEntityName.WorkOrder].includes(entity))
            entity = ActivityEntityName.Job;

        return entity
            ? `${UserSettingKey.FilesDefaultView}-${entity?.toLocaleLowerCase()}`
            : null;
    }

    protected addSelectedCommand: Command = new Command({ _id: FilesDashboardCommandIds.AddSelected, count: 0, label: 'Add Selected', disabled: true, action: () => this.onModuleHeaderCommand(this.addSelectedCommand) });
    protected selectAllCommand: Command = new Command({ _id: FilesDashboardCommandIds.SelectAll, count: 0, label: 'Select All', action: () => this.onModuleHeaderCommand(this.selectAllCommand) });
    protected downloadSelectedCommand: Command = new Command({ _id: FilesDashboardCommandIds.DownloadSelected, count: 0, label: 'Download Selected', disabled: true, action: () => this.onModuleHeaderCommand(this.downloadSelectedCommand) });
    protected downloadAllCommand: Command = new Command({ _id: FilesDashboardCommandIds.DownloadAll, label: 'Download All', action: () => this.onModuleHeaderCommand(this.downloadAllCommand) });
    protected clearSelectedCommand: Command = new Command({ _id: FilesDashboardCommandIds.ClearSelected, label: 'Clear Selected', action: () => this.onModuleHeaderCommand(this.clearSelectedCommand) });
    protected deleteSelectedFilesCommand: Command = new Command({ _id: FilesDashboardCommandIds.DeleteSelectedFiles, label: 'Delete Selected Files', disabled: true, action: () => this.onModuleHeaderCommand(this.deleteSelectedFilesCommand) })

    protected menuCommands = [
        this.downloadSelectedCommand,
        this.downloadAllCommand
    ];

    protected headerApi: ModuleHeaderApi = null;

    protected viewEntity: ViewEntity = null;
    protected viewOptions: ViewOptions = null;
    protected filterValue: FilesFilterDataSourceValue = null;

    protected attachments: MetadataAttachment[] = [];
    protected groups: FileGroup[] = [];
    protected attachment: Attachment = null;
    protected moduleHeaderForm = new FormControl(null);
    protected expandedAttachment: Attachment = null;
    protected settingsLoaded = false;

    protected readonly zipper = new FilesZipService();

    protected DialogType = DialogType;
    protected FilesDisplayMode = FilesDisplayMode;

    protected attachmentContext: AttachmentContext;
    private showAllCurrentValue = false;

    constructor(
        @Optional() private config: DynamicDialogConfig,
        @Optional() private dialgRef: DynamicDialogRef,
        protected dialogService: DialogService,
        protected filesService: FilesService,
        protected selectionService: SelectionService<string>,
        protected activeService: ActiveService<AttachmentComponent>,
        private confirmService: ConfirmServicePro,
        private currentUser: CurrentUserService,
        private cdr: ChangeDetectorRef,
        private route: ActivatedRoute,
        private fileExtractService: FilenameExtractService,
        private rightsService: RightsService,
    ) {

        /** This is used for sandbox testing at the moment */
        const { context } = route.snapshot.data;

        if (context) {
            this.context = context;
            //  this.loadFiles();
            this.loadSettings();
        }

        if (config) {
            const { context } = config.data;
            this.context = context;
            //   this.loadFiles();
            this.loadSettings();
        }

        const metadata$ = this.metadataService
            .getMetadata(ActivityEntityName.File)
            .pipe(
                tap(x => this.attachmentMetadata = x)
            );

        this.selectionService
            .selection$
            .pipe(
                untilDestroyed(this),
                tap(x => {

                    const count = x?.length ?? 0;

                    [
                        this.addSelectedCommand,
                        this.downloadSelectedCommand,
                        this.deleteSelectedFilesCommand
                    ]
                        .filter(x => !!x)
                        .forEach(command => {
                            command.disabled = count == 0;
                            command.count = count;
                        });

                    this.clearSelectedCommand.hidden = count === 0;

                })
            )
            .subscribe();

        this.activeService
            .change$
            .pipe(
                untilDestroyed(this),
                tap(x => {


                    if (this.dialogService.modalDialog !== DialogType.View) {

                        if (this.context?.displayMode === FilesDisplayMode.Picker) {
                            this.expandedAttachment = this.activeService.activeItem?.attachment;
                        } else if (this?.context?.displayMode === FilesDisplayMode.Full) {
                            this.dialogService.showDialog(this.activeService.activeItem ? DialogType.Edit : DialogType.None);
                        } else {
                            this.dialogService.showModalDialog(this.activeService.activeItem ? DialogType.Edit : DialogType.None);
                        }

                    }

                    this.attachment = this.activeService?.activeItem?.attachment;
                })
            )
            .subscribe();

        this.moduleHeaderForm
            .valueChanges
            .pipe(
                untilDestroyed(this),
                tap(x => {
                    this.viewOptions = x?.viewOptions;
                    this.viewEntity = x?.view;
                    this.headerApi.addCommand.disabled = this?.viewEntity?._id === 'RelatedFiles';
                    this.filterValue = x?.filterValue;
                    this.buildGroups();

                    if (this.settingsLoaded) {
                        this.settingsLoaded = false;
                    } else {
                        console.log('view changed', { ...x, ...this.context });
                        this.saveSettings(x?.viewOptions);
                    }

                    const showAll = this.viewOptions?.showAll ?? false;
                    if (this.entity === ActivityEntityName.WorkOrder && this.showAllCurrentValue !== showAll) {
                        this.loadFiles(showAll);
                    }
                })
            )
            .subscribe();

        this.filesService
            .files$
            .pipe(
                untilDestroyed(this),
                withLatestFrom(metadata$),
                tap(([attachments, entity]) => {
                    this.attachments = attachments;

                    this?.headerApi?.update({
                        filterDataSource: getFilesFilterDataSource(entity, attachments, this.context.allowTypes),
                        filterValue: this?.filterValue ?? null,
                    });

                    // Close the opened detail panel
                    this.activeService.deactivate(this.activeService.activeItem);

                    this.buildGroups();
                    this.cdr.markForCheck();
                })
            )
            .subscribe();

        this.filesService
            .fileChanged$
            .pipe(
                untilDestroyed(this),
                filter(attachment => !!attachment),
                tap(attachment => {


                    if (this.attachment?._id === attachment?._id) {
                        this.attachment = { ...attachment };
                    }

                    if (this.activeService?.activeItem?.attachment?._id === attachment?._id) {
                        this.activeService.activeItem.attachment = attachment;
                    }



                    this.cdr.markForCheck();

                })
            )
            .subscribe();

        this.attachmentContext = getAttachmentContext(this.entity, this.rightsService);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes.context) {
            if (changes.context.previousValue?.entityId !== changes.context.currentValue?.entityId) {
                //  this.loadFiles();
                this.loadSettings();
            }

            if (this.headerApi)
                this.onModuleHeaderInit(this.headerApi);
        }

        if (changes.allowMultiSelect) {
            this.selectionService.allowMultiSelect = this.allowMultiSelect;
        }
    }


    async onModuleHeaderInit(api: ModuleHeaderApi) {

        this.headerApi = api;

        const attachments = !!this.attachmentMetadata
            ? this.attachmentMetadata
            : await firstValueFrom(this.metadataService.getMetadata(ActivityEntityName.File));

        const groupable = Enumerable
            .from(attachments.fields)
            .where(x => x.groupable)
            .orderBy(x => x.name)
            .toArray();

        const sortable = Enumerable
            .from(attachments.fields)
            .where(x => x.sortable)
            .orderBy(x => x.name)
            .toArray();

        this.viewEntity = { _id: 'EntityFiles', name: 'Files', default: true, canEdit: false, emptyMessage: 'No files were found.', icon: 'fa-regular fa-files' };

        let sharing = {
            type: ViewOptionType.Dropdown,
            title: 'Shared With',
            dbName: 'sharing',
            items: [
                {
                    _id: 'All',
                    name: 'All',
                },
                {
                    _id: ActivityShareType.Customer,
                    name: 'Customer',
                },
                {
                    _id: ActivityShareType.Field,
                    name: 'Field',
                },
                {
                    _id: ActivityShareType.NotShared,
                    name: 'Not Shared',
                }
            ],
            defaultValue: 'All'
        } as ViewOptionSchema;

        if (this.isContractorsPortal) {
            sharing = {
                ...sharing,
                items: [
                    {
                        _id: ActivityShareType.Field,
                        name: 'Field',
                    },
                ],
                defaultValue: 'Field'
            } as ViewOptionSchema;
        }

        const optionBuilder =
            ViewOptionsBuilder
                .new()
                .addSection({
                    dbName: '',
                })
                .addOption({
                    type: ViewOptionType.Dropdown,
                    title: 'Group',
                    dbName: 'groupBy',
                    hidden: !groupable.length,
                    items: Enumerable
                        .from(groupable)
                        .select(x => {
                            return { _id: x.name, name: x.displayName };
                        })
                        .toArray(),
                    defaultValue: ''
                })
                .addOption(sharing, [ActivityEntityName.Job, ActivityEntityName.WorkOrder].includes(this.context.entity))
                .addDivider()
                .addOption({
                    type: ViewOptionType.Dropdown,
                    title: 'Sort',
                    dbName: 'sortBy',
                    hidden: !sortable.length,
                    items: Enumerable
                        .from(sortable)
                        .select(x => {
                            return { _id: x.name, name: x.displayName };
                        })
                        .toArray(),
                    defaultValue: ''
                })
                .addOption({
                    type: ViewOptionType.Dropdown,
                    title: 'Direction',
                    dbName: 'sortOrder',
                    hidden: false,
                    items: [
                        { _id: 'asc', name: 'Ascending' },
                        { _id: 'desc', name: 'Descending' }
                    ],
                    defaultValue: 'asc'
                })
                .addDivider()
                .addOption({
                    type: ViewOptionType.Boolean,
                    title: 'Show File Activities Only',
                    dbName: 'showFileActivitiesOnly',
                    defaultValue: false
                })

        if (this.entity === ActivityEntityName.Job || this.entity === ActivityEntityName.WorkOrder) {
            const isJob = this.entity === ActivityEntityName.Job;
            optionBuilder.addOption({
                type: ViewOptionType.Boolean,
                title: 'Show All',
                dbName: 'showAll',
                defaultValue: isJob,
                disabled: isJob
            });
        }

        const viewOptionsSchema = optionBuilder
            .finishSection()
            .build();

        if (!this.viewOptions) {
            this.viewOptions = {
                groupBy: 'entityName',
                sortBy: 'createdDate',
                sortOrder: 'desc',
                showFileActivitiesOnly: false,
                sharing: this.isContractorsPortal ? ActivityShareType.Field : 'All'
            }
        }

        this.downloadSelectedCommand.label = this?.context?.displayMode === FilesDisplayMode.Sidebar
            ? 'Download'
            : 'Download Selected';

        const readonly = this.context?.readonly;
        const moreCommands = [
            this.allowMultiSelect ? this.selectAllCommand : null,
            this.downloadSelectedCommand,
            this.downloadAllCommand,
            readonly ? null : this.deleteSelectedFilesCommand,
            this.clearSelectedCommand,

        ].filter(x => !!x);

        api.update({
            primaryCommands: [
                this.downloadSelectedCommand
            ],
            moreCommands: moreCommands,
            // views: [...views],
            viewOptionsSchema,
            viewOptionDefaults: this.viewOptions,
            viewOptions: this.viewOptions,
            filterDisabled: this.attachments?.length === 0,
            viewOptionsDisabled: false,//Never disabled as we can potentially show all which may bring further files
            filterValue: this?.filterValue ?? null,
            title: 'Files',
            size: this?.context?.displayMode === FilesDisplayMode.Sidebar
                ? ButtonSize.Small
                : ButtonSize.Small,
        });

        api.viewEditCommand.hidden = true;
        api.viewCreateCommand.hidden = true;
        api.addCommand.disabled = this?.viewEntity?._id === 'RelatedFiles';
        api.closeCommand.hidden = this?.context?.displayMode !== FilesDisplayMode.Picker;

        switch (this?.context?.displayMode) {
            case FilesDisplayMode.Claims:
                this.addSelectedCommand.label = 'Link Selected';
                this.addSelectedCommand.iconLeft = 'fa-regular fa-link-simple'
                api.addCommand.hidden = true;

                api.update({
                    title: null,
                    titleCommands: [
                        this.addSelectedCommand,
                    ],
                    primaryCommands: [],
                    moreCommands: [],
                });
                break;

            case FilesDisplayMode.Picker:
                api.addCommand.hidden = true;
                api.update({
                    primaryCommands: [],
                    moreCommands: [
                        this.clearSelectedCommand
                    ]
                });
                break;
        }


        if (!api.addCommand.hidden && readonly === true) {
            api.addCommand.hidden = true;
        }

        this.cdr.markForCheck();
    }

    async onModuleHeaderCommand(command: Command): Promise<void> {

        switch (command?._id) {
            case ModuleViewHeaderCommandIds.Add:
                this.dialogService.showModalDialog(DialogType.New);
                break;

            case ModuleViewHeaderCommandIds.Refresh:
                this.loadFiles(this.showAllCurrentValue);
                break;

            case ModuleViewHeaderCommandIds.ViewCreate:
                break;

            case ModuleViewHeaderCommandIds.ViewEdit:
                break;

            case ModuleViewHeaderCommandIds.ViewChange:
                const { view, viewOptions } = command.data();

                this.viewEntity = view;
                this.viewOptions = viewOptions;

                this.buildGroups();
                break;

            case FilesDashboardCommandIds.DownloadAll: {
                const zipName = `${this.entityName}_files_${DateTime.local().toFormat('dd-MM-yyyy')}.zip`;

                try {
                    this.downloadAllCommand.disabled = true;
                    this.downloadSelectedCommand.disabled = true;
                    this.zipper.downloadGroups(zipName, this.groups);

                } finally {
                    this.downloadAllCommand.disabled = false;
                    this.downloadSelectedCommand.disabled = this.selectionService.selection?.length == 0;
                }

                break;
            }

            case FilesDashboardCommandIds.DownloadSelected: {

                const zipName = `${this.entityName}_selected_files_${DateTime.local().toFormat('dd-MM-yyyy')}.zip`;

                try {
                    this.downloadAllCommand.disabled = true;
                    this.downloadSelectedCommand.disabled = true;

                    const attms = this.filesService.getByIds(this.selectionService.selection);

                    const updatedAttms = await this.fileExtractService.updateAttachmentNames(attms);

                    this.zipper.downloadAttachments(zipName, updatedAttms);

                } finally {
                    this.downloadAllCommand.disabled = false;
                    this.selectionService.clear();
                }

                break;
            }

            case FilesDashboardCommandIds.DeleteSelectedFiles: {
                const selection = this.filesService.getByIds(this.selectionService.selection);

                const nonFiles = Enumerable.from(selection).any(x => !x.isFile);
                const deletable = selection.filter(x => canUserChangeFile(x));
                const readOnly = selection.length - deletable.length;
                if (nonFiles)
                    this.confirmService
                        .confirm('Cannot Delete Selected Files', 'You have selected non-file attachments. Only files can be deleted. Delete other attachments from their source (i.e. Tasks or Activities)', 'info', '', '', 'Ok', true);
                else if (!deletable.length)
                    this.confirmService
                        .confirm('Cannot Delete Selected Files', 'The selected files came from an integration and are read-only.', 'info', '', '', 'Ok', true);
                else
                    this.confirmService
                        .confirm('Delete Files', deleteFilesQuestion(deletable.length, readOnly), 'danger')
                        .then((confirm) => {

                            if (!confirm) return;
                            try {
                                this.deleteSelectedFilesCommand.disabled = true;
                                this.downloadSelectedCommand.disabled = true;
                                this.deleteFileActivities(selection);
                            }
                            finally {
                                this.selectionService.clear();
                            }
                        });
                break;
            }

            case FilesDashboardCommandIds.ClearSelected:
                this.selectionService.clear();
                break;

            case FilesDashboardCommandIds.AddSelected: {
                this.addSelected();
                break;
            }

            case FilesDashboardCommandIds.SelectAll: {
                this.selectAll();
                break;
            }

            case ModuleViewHeaderCommandIds.Close: {
                this?.dialgRef?.close();
                break;
            }
        }
    }

    protected onAddFile(entity: ActivityEntityName) {
        this.dialogService.showModalDialog(DialogType.New);
    }

    protected onCreateActivity(activity: Partial<Activity>) {

        // Clear the thumbnail so it doesn't get sent to the server
        Enumerable
            .from(activity?.attachments)
            .where(x => !!x)
            .forEach(x => x.thumbnail = null);

        this.filesService
            .addActivity(activity as Activity);
    }

    protected onDeleteActivity(attachment: MetadataAttachment) {

        if (!canUserChangeFile(attachment)) return;

        this.confirmService
            .confirm('Delete File', `Are you sure you want to delete the file?`, 'danger')
            .then((confirm) => {

                if (!confirm) return;
                this.deleteFileActivities([attachment]);
            });

    }

    /**
     * Deletes the files the user may change and leaves the read-only ones, then says what happened: an error toast
     * for any delete the server refused, and an info toast when read-only files were left behind.
     */
    private async deleteFileActivities(attachments: MetadataAttachment[]): Promise<void> {

        const deletable = attachments.filter(x => canUserChangeFile(x));
        const readOnly = attachments.length - deletable.length;

        const failures = await deleteFiles(deletable, x => this.filesService.deleteActivityAttachment(x.entityId, x._id));

        if (failures.length) {
            const reasons = [...new Set(failures.map(x => x.reason))].join(' ');
            const summary = deletable.length === 1
                ? 'File not deleted'
                : `${failures.length} of ${deletable.length} files not deleted`;
            this.toastService.error(summary, reasons);
        }

        if (readOnly)
            this.toastService.info(`${countFiles(deletable.length - failures.length)} deleted`, `${countFiles(readOnly)} came from an integration and ${readOnly === 1 ? 'was' : 'were'} left, because ${readOnly === 1 ? 'it is' : 'they are'} read-only.`);
    }

    private loadFiles(showAll: boolean = false) {
        this.showAllCurrentValue = showAll;
        this.filesService.getFiles(this.entity, this.entityId, showAll);
    }

    private loadSettings() {
        if (!this.context) {
            return;
        }

        const key = this.settingsKey;
        if (this.isTenant) {
            this.currentUser
                .get<ViewOptions>(key)
                .pipe(
                    //  filter(x => !!x),
                    tap(x => {
                        this.settingsLoaded = true;
                        this.viewOptions = x ?? {
                            groupBy: 'entityName',
                            sortBy: 'createdDate',
                            sortOrder: 'desc',
                            showFileActivitiesOnly: false,
                            sharing: 'All'
                        };

                        this.showAllCurrentValue = this.viewOptions?.showAll ?? false;
                        if (this.context?.entity === ActivityEntityName.Job) {
                            this.viewOptions.showAll = true;
                        }

                        this.moduleHeaderForm.patchValue({
                            ...this.moduleHeaderForm.value,
                            viewOptions: this.viewOptions
                        });

                        this.loadFiles(this.showAllCurrentValue);
                    }),
                    catchError(() => {
                        this.loadFiles(this.showAllCurrentValue);
                        return [];
                    })
                )
                .subscribe();
        } else if (this.isClientsPortal) {
            this.loadFiles(true);
        } else if (this.isContractorsPortal) {
            this.loadFiles(this.showAllCurrentValue);
        } else {
            return;
        }
    }

    private saveSettings(options: ViewOptions) {
        if (!this.context || !this.isTenant) return;
        const key = this.settingsKey;
        this.currentUser
            .set<ViewOptions>(key, options)
            .subscribe();
    }

    private buildGroups() {

        const viewOptions = this.viewOptions;

        const groupBy = viewOptions?.groupBy ?? 'entityName';
        const sortBy = viewOptions?.sortBy ?? 'createdDate';
        const sortOrder = viewOptions?.sortOrder ?? 'asc';
        const timezone = this.context?.timezone || 'utc';

        const noGroupTitle = 'No Group';

        // Array fields (tags, taskItems) explode to one row per entry so a file can sit in several groups.
        const groupEntries = (x: MetadataAttachment): Entity[] | null => {
            const value = (x as Record<string, unknown>)[groupBy];
            return Array.isArray(value) ? value as Entity[] : null;
        };
        const explode = this.attachments.some(x => !!groupEntries(x));

        const safeAttachments: MetadataAttachmentWithGroup[] = explode
            ? Enumerable
                .from(this.attachments)
                .selectMany(x => {
                    const entries = groupEntries(x) ?? [];
                    const rows: (Entity | null)[] = entries.length ? entries : [null];
                    return rows.map(groupEntry => ({ ...x, groupEntry } as MetadataAttachmentWithGroup));
                })
                .toArray()
            : this.attachments as MetadataAttachmentWithGroup[];

        const filterValue = {
            ...this.filterValue,
        };

        const allowedTypes = this.context?.allowTypes;
        if (allowedTypes?.length) {
            let allowFilter: Entity[] = [];

            for (const type of allowedTypes) {
                switch (type) {
                    case AllowedPickType.Files:
                        allowFilter.push({ _id: 'File', name: 'File' });
                        break;
                    case AllowedPickType.Images:
                        allowFilter.push({ _id: 'Image', name: 'Image' });
                        break;
                    case AllowedPickType.Forms:
                        allowFilter.push({ _id: 'Form', name: 'Form' });
                }
            }

            if (allowFilter.length) {
                if (!filterValue?.type) {
                    filterValue.type = [];
                }

                filterValue.type.push(...allowFilter);
            }
        }

        let groups = Enumerable
            .from(safeAttachments)
            .where(x => {
                const passesFile = viewOptions?.showFileActivitiesOnly ? x.isFile : true;

                const passesCreatedBy = filterValue?.createdBy?.length
                    ? Enumerable
                        .from(filterValue.createdBy).any(f => f._id === x.createdBy)
                    : true;

                const passesTags = filterValue?.tags?.length
                    ? Enumerable
                        .from(filterValue.tags)
                        .any(f => Enumerable.from(x.tags).any(t => t._id === f._id))
                    : true;

                const passesEntity = filterValue?.entityName?.length
                    ? Enumerable
                        .from(filterValue.entityName).any(f => f._id === x.entityName)
                    : true;

                const passesType = filterValue?.type?.length
                    ? Enumerable
                        .from(filterValue.type).any(f => f._id === x.type)
                    : true;

                const passesKind = filterValue?.kind?.length
                    ? Enumerable
                        .from(filterValue.kind).any(f => f._id === x.kind)
                    : true;

                const createdDateFilter = filterValue?.createdDate
                    ? FiltersRelativeDateUtility.getFilter(filterValue?.createdDate) as any
                    : null;

                let passesDate = true;

                if (createdDateFilter && x.createdDate) {
                    const dt = DateUtility.getDateTime(x.createdDate, 'utc').toJSDate();
                    const { $gte, $lte, $lt } = createdDateFilter;

                    const isGTE = $gte
                        ? $gte && dt >= $gte
                        : true;

                    const isLTE = $lte
                        ? $lte && dt <= $lte
                        : true;

                    const isLT = $lt
                        ? $lt && dt < $lt
                        : true;

                    passesDate = isGTE && isLTE && isLT;
                }

                return passesFile && passesCreatedBy && passesKind && passesTags && passesType && passesEntity && passesDate;

            })
            .where(x => {
                //Destructuring nulls still returns a type error
                const sharing = this?.viewOptions?.sharing;
                if (!sharing) return true;

                if (sharing === ActivityShareType.NotShared)
                    return x?.effectiveSharing?.shared?.length == 0;

                if (sharing === ActivityShareType.Customer)
                    return x?.effectiveSharing?.shared?.includes(ActivityShareType.Customer);

                if (sharing === ActivityShareType.Field)
                    return x?.effectiveSharing?.shared?.includes(ActivityShareType.Field);

                return true; x.effectiveSharing
            })
            .groupBy(x => {
                if (explode) return x.groupEntry?.name ?? noGroupTitle;

                const group = x[groupBy];

                if (groupBy === 'createdDate') {
                    if (!group) return noGroupTitle;
                    const dt = DateUtility.getDateTime(group, timezone).startOf('day');
                    return dt.toISO();
                }

                return group;
            })
            .select(x => {

                let groupName = x.key();
                let groupKey = groupName;

                if (groupBy === 'createdDate') {

                    if (groupName === noGroupTitle) {
                        groupKey = DateTime.local().minus({ years: 1000 }).toISO();
                    } else
                        groupName = DateUtility.getDateTime(groupName, timezone).toRelativeCalendar();
                }

                return {
                    title: this.capitalizeFirstLetter(groupName),
                    type: groupKey,
                    attachments: sortOrder === 'asc'
                        ? x.orderBy(x => x[sortBy]).toArray()
                        : x.orderByDescending(x => x[sortBy]).toArray(),
                    hideTags: groupBy == 'tags'
                } as FileGroup;

            })
            .orderBy(x => {
                return x.type
            })
            .toArray();

        if (groupBy === 'createdDate') {
            groups = groups.reverse();
        }

        const noGroup = Enumerable
            .from(groups)
            .where(x => x.title === noGroupTitle)
            .toArray();

        const otherGroups = Enumerable.from(groups)
            .where(x => x.title !== noGroupTitle)
            .toArray();


        this.groups = [...otherGroups, ...noGroup];
        this.toggleHeaderButtons();

        this.cdr.markForCheck();
    }

    private toggleHeaderButtons() {
        this?.headerApi?.update({
            filterDisabled: !this.attachments.length,
            viewOptionsDisabled: false,//Never disabled as we can potentially show all which may bring further files
        })
        this.downloadAllCommand.disabled = !this.attachments.length;
    }

    /// Add selected attachments to the component
    /// If the component is in picker mode, find attachments with type 'Form' and change them to 'File'
    /// because people mainly want Files in picker mode (e.g when adding attachments to an email)
    protected async addSelected() {
        const selection = this.filesService.getByIds(this.selectionService.selection);
        const updatedAttachments = await this.fileExtractService.updateAttachmentNames(selection);

        if (this.context?.displayMode === FilesDisplayMode.Picker) {
            const safeSelection = Enumerable
                .from(updatedAttachments)
                .select(x => {

                    if (x.path.endsWith('.pdf') && (x.type === 'Form' || x.kind === ActivityKind.Form)) {
                        return { ...x, kind: ActivityKind.File, type: 'File', _id: v4() } as MetadataAttachment;
                    }

                    return x;
                })
                .toArray();
            this.dialgRef.close(safeSelection);
        }
        else {
            this.dialgRef.close(updatedAttachments);
        }
    }

    protected onCancel() {
        this.dialgRef.close();
    }

    protected onExpand(item: Attachment) {
        if (this.context.displayMode !== FilesDisplayMode.Picker) return;
        this.expandedAttachment = item;
        this.dialogService.showModalDialog(DialogType.Media);

    }

    protected onAttachmentUpdated(attachment: Attachment) {

        const updatedThumbnail = getFileThumbnailUrl(attachment, true);

        for (let index = 0; index < this.groups.length; index++) {
            const attachments = this.groups[index].attachments;
            const found = attachments.findIndex(x => x._id === attachment._id);
            if (found >= 0) {
                attachments[found] = {
                    ...attachments[found],
                    updatedAt: attachment.updatedAt,
                    thumbnail: updatedThumbnail,
                };
                break;
            }
        }

        if (this.attachment?._id === attachment?._id) {
            this.attachment = {
                ...this.attachment,
                updatedAt: attachment.updatedAt,
                thumbnail: updatedThumbnail,
            }
        }

        this.cdr.markForCheck();
    }

    private selectAll() {
        const all = this.groups.map(x => x.attachments ?? []).flat().map(x => x._id);
        this.selectionService.selectMany(all);
    }

    private capitalizeFirstLetter(text: string): string {
        if (!text || text.length === 0) return text;

        const firstChar = text.charAt(0);
        if (firstChar === firstChar.toUpperCase()) {
            return text;
        } else {
            return firstChar.toUpperCase() + text.slice(1);
        }
    }
}